import { useEffect, useRef, useState } from 'react';
import { formatCents } from '../../shared/money';
import { formatPhone } from '../../shared/format';
import { Badge, Button, useToast } from '../ui/kit';

/**
 * Mega Wireless: the dealer portal where carrier bill payments (refills) are made.
 *
 * It opens in its own window on the right side of the screen, so the STORE OS tab never
 * changes. It can't sit inside the STORE OS page itself: Mega Wireless keeps its login in a
 * cookie that browsers drop for a site shown inside another site, so it would keep asking to
 * log in. In its own window the login sticks (Mega Wireless keeps it for a day).
 */
export const MEGA_WIRELESS_URL = 'https://dealer.megawirelessus.com/home#Refills';

const WINDOW_NAME = 'storeos_megawireless';
/** How long STORE OS stays unlocked while the cashier is working in the Mega Wireless window. */
const KEEP_AWAKE_MS = 20 * 60_000;

// One Mega Wireless window per STORE OS tab, kept outside React so it survives leaving Bill pay.
let portal: Window | null = null;
let shownAt = 0;

export function megaWirelessOpen(): boolean {
  return portal !== null && !portal.closed;
}

/** Opens Mega Wireless on the right side of the screen, or brings it back to the front. */
export function showMegaWireless(): boolean {
  shownAt = Date.now();
  if (portal && !portal.closed) {
    portal.focus();
    return true;
  }
  const scr = window.screen as Screen & { availLeft?: number; availTop?: number };
  const sw = scr.availWidth || window.outerWidth;
  const sh = scr.availHeight || window.outerHeight;
  const width = Math.min(Math.max(Math.round(sw * 0.55), 900), 1200, Math.max(sw - 380, 600));
  const left = (scr.availLeft ?? 0) + Math.max(0, sw - width);
  const top = scr.availTop ?? 0;
  const win = window.open(MEGA_WIRELESS_URL, WINDOW_NAME, `popup=yes,width=${width},height=${sh},left=${left},top=${top}`);
  if (!win) return false;
  try {
    win.opener = null; // Mega Wireless gets no handle back into STORE OS.
  } catch {
    // The window was already showing Mega Wireless; nothing to cut.
  }
  portal = win;
  return true;
}

/** Copies text in the same tap (before the Mega Wireless window takes the focus). */
function copyText(text: string): boolean {
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.setAttribute('readonly', '');
  ta.style.position = 'fixed';
  ta.style.top = '0';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.select();
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch {
    ok = false;
  }
  ta.remove();
  return ok;
}

/**
 * The Mega Wireless step on Bill pay: open it beside STORE OS (the phone number is copied,
 * ready to paste), do the refill there, then come back here for the confirmation number.
 */
export function MegaWirelessPanel({ account, amountCents, onClosed }: { account: string; amountCents: number | null; onClosed?: () => void }) {
  const toast = useToast();
  const [open, setOpen] = useState(megaWirelessOpen);
  const onClosedRef = useRef(onClosed);
  onClosedRef.current = onClosed;
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const digits = account.replace(/\D/g, '');
  const amountText = amountCents && amountCents > 0 ? (amountCents / 100).toFixed(2) : '';

  useEffect(() => {
    let wasOpen = megaWirelessOpen();
    setOpen(wasOpen);
    const watch = window.setInterval(() => {
      const nowOpen = megaWirelessOpen();
      if (nowOpen === wasOpen) return;
      wasOpen = nowOpen;
      setOpen(nowOpen);
      if (!nowOpen) {
        toastRef.current('Mega Wireless closed. Type its confirmation number below.', 'info');
        onClosedRef.current?.();
      }
    }, 800);
    // Taps and typing in the Mega Wireless window don't count as activity here, so STORE OS
    // would lock in the middle of a refill. While that window is in use (for up to
    // KEEP_AWAKE_MS), nudge the idle timer: useIdle counts a wheel event as activity.
    const awake = window.setInterval(() => {
      if (megaWirelessOpen() && !document.hasFocus() && Date.now() - shownAt < KEEP_AWAKE_MS) {
        window.dispatchEvent(new Event('wheel'));
      }
    }, 30_000);
    return () => {
      window.clearInterval(watch);
      window.clearInterval(awake);
    };
  }, []);

  /** Copies `text` (if given), then shows Mega Wireless. */
  const show = (text?: string, what?: string) => {
    const copied = text ? copyText(text) : false;
    if (!showMegaWireless()) {
      toast('The browser blocked the Mega Wireless window. Allow pop-ups for STORE OS, then tap again.', 'error');
      return;
    }
    setOpen(true);
    if (text && what) toast(copied ? `${what} copied. Paste it in Mega Wireless.` : `Couldn’t copy the ${what.toLowerCase()}.`, copied ? 'good' : 'error');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 16, borderRadius: 12, border: '1px solid var(--border-strong)', background: 'var(--cloud)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span aria-hidden="true" style={{ width: 44, height: 44, flex: 'none', borderRadius: 10, background: 'var(--navy)', color: 'var(--white)', display: 'grid', placeItems: 'center', fontWeight: 800, fontSize: 15 }}>MW</span>
        <div className="grow" style={{ minWidth: 220 }}>
          <div className="strong">Pay it in Mega Wireless</div>
          <div className="small muted">Opens beside STORE OS in its own window, so you never leave this tab. Log in there once and Mega Wireless keeps you signed in for the day.</div>
        </div>
        {open ? <Badge tone="green" icon="check">Window open</Badge> : null}
      </div>
      <div className="row-wrap">
        <Button variant="primary" size="lg" icon="arrowRight" onClick={() => (digits.length >= 10 ? show(digits, 'Phone number') : show())}>
          {open ? 'Show Mega Wireless' : 'Open Mega Wireless'}
        </Button>
        <Button disabled={digits.length < 4} onClick={() => show(digits, 'Phone number')}>
          Copy phone{digits.length >= 4 ? ` ${formatPhone(digits)}` : ''}
        </Button>
        <Button disabled={!amountText} onClick={() => show(amountText, 'Amount')}>
          Copy amount{amountText ? ` ${formatCents(amountCents ?? 0)}` : ''}
        </Button>
      </div>
    </div>
  );
}
